import { useEffect, useMemo, useState } from 'react';
import {
  Banner,
  Button,
  Card,
  Checkbox,
  Modal,
  Page,
  SkeletonBodyText,
  Text,
  TextField,
} from '@shopify/polaris';
import { MenuSelect } from '../components/MenuSelect';
import { MetricCard } from '../components/MetricCard';
import { StatusBadge } from '../components/StatusBadge';

type IdentityType = 'telegram_user_id' | 'phone';
type TelegramRole = 'admin' | 'viewer';
type RuleStatus = 'active' | 'disabled';

type TelegramAccessRule = {
  id: number;
  identityType: IdentityType;
  identifier: string;
  role: TelegramRole;
  label?: string;
  status: RuleStatus;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type TelegramSettingsSnapshot = {
  status: string;
  summary: {
    adminCount: number;
    viewerCount: number;
    activeCount: number;
    disabledCount: number;
    botRuntimeConfigured: boolean;
    miniAppRuntimeConfigured: boolean;
  };
  rules: TelegramAccessRule[];
  runtime: {
    botTokenConfigured: boolean;
    botTokenFingerprint?: string | null;
    botTokenSource?: string;
    miniAppUrlConfigured: boolean;
    miniAppUrl?: string;
    miniAppUrlSource?: string;
    webhookUrlConfigured: boolean;
    webhookUrl?: string;
    webhookUrlSource?: string;
  };
};

const INITIAL_FORM = {
  identityType: 'telegram_user_id' as IdentityType,
  identifier: '',
  role: 'viewer' as TelegramRole,
  label: '',
  status: 'active' as RuleStatus,
};

const INITIAL_RUNTIME_FORM = {
  miniAppUrl: '',
  webhookUrl: '',
  botToken: '',
  clearBotToken: false,
};

const IDENTITY_OPTIONS = [
  { label: 'Telegram user ID', value: 'telegram_user_id', helpText: 'Вхід без телефону, якщо ID є у списку.' },
  { label: 'Телефон', value: 'phone', helpText: 'Бот попросить поділитися контактом.' },
];

const ROLE_OPTIONS = [
  { label: 'Адмін', value: 'admin', helpText: 'Може змінювати налаштування.' },
  { label: 'Перегляд', value: 'viewer', helpText: 'Бачить стан, але не керує системою.' },
];

const ROLE_ACTION_OPTIONS = ROLE_OPTIONS;

const STATUS_OPTIONS = [
  { label: 'Активний', value: 'active', helpText: 'Правило дає доступ.' },
  { label: 'Вимкнений', value: 'disabled', helpText: 'Залишається в історії, але доступ не дає.' },
];

function cleanupPhone(value: string) {
  return value.trim().replace(/[()\s-]/g, '');
}

function validateForm(form: typeof INITIAL_FORM) {
  const identifier = form.identityType === 'phone'
    ? cleanupPhone(form.identifier)
    : form.identifier.trim().replace(/\s+/g, '');

  if (form.identityType === 'telegram_user_id' && !/^\d{5,20}$/.test(identifier)) {
    return 'Telegram user ID має містити 5-20 цифр.';
  }

  if (form.identityType === 'phone' && !/^\+\d{8,15}$/.test(identifier)) {
    return 'Телефон має бути у форматі +380XXXXXXXXX.';
  }

  return '';
}

function identityLabel(identityType: IdentityType) {
  return identityType === 'telegram_user_id' ? 'Telegram ID' : 'Телефон';
}

function roleLabel(role: TelegramRole) {
  return role === 'admin' ? 'Адмін' : 'Перегляд';
}

function sourceLabel(source?: string) {
  if (source === 'admin') return 'збережено в адмінці';
  if (source === 'env') return 'прочитано із серверних змінних';
  return 'ще не задано';
}

function ruleStatusBadge(status: RuleStatus) {
  return status === 'active'
    ? <StatusBadge status="live" label="Активний" />
    : <StatusBadge status="disabled" label="Вимкнений" />;
}

function formatDateTime(value?: string | null) {
  if (!value) return '—';
  return new Date(value).toLocaleString('uk-UA');
}

function helperText(identityType: IdentityType) {
  if (identityType === 'telegram_user_id') {
    return 'Якщо є Telegram ID, Mini App зможе пропустити телефон після перевірки службових даних Telegram.';
  }

  return 'Якщо вказано телефон, бот має попросити користувача поділитися контактом.';
}

function runtimeStatusText(snapshot: TelegramSettingsSnapshot) {
  const missing: string[] = [];
  if (!snapshot.runtime.botTokenConfigured) missing.push('ключ бота');
  if (!snapshot.runtime.miniAppUrlConfigured) missing.push('адресу Mini App');
  if (!snapshot.runtime.webhookUrlConfigured) missing.push('адресу подій бота');

  if (missing.length === 0) {
    return 'Усе підключено: бот, Mini App і адреса подій готові до роботи.';
  }

  return `Ще треба задати: ${missing.join(', ')}.`;
}

export function TelegramSettingsPage({ api, notify, readOnly = false }: any) {
  const [snapshot, setSnapshot] = useState<TelegramSettingsSnapshot | null>(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [runtimeForm, setRuntimeForm] = useState(INITIAL_RUNTIME_FORM);
  const [runtimeModalOpen, setRuntimeModalOpen] = useState(false);
  const [runtimeSaving, setRuntimeSaving] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState<TelegramAccessRule | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const refresh = async () => {
    setLoading(true);
    try {
      const nextSnapshot = await api.getTelegramSettings();
      setSnapshot(nextSnapshot);
      setError('');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Не вдалося завантажити Telegram налаштування');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const activeRules = useMemo(() => (
    (snapshot?.rules || []).filter((rule) => rule.status === 'active')
  ), [snapshot?.rules]);

  const openRuntimeModal = () => {
    setRuntimeForm({
      miniAppUrl: snapshot?.runtime.miniAppUrl || '',
      webhookUrl: snapshot?.runtime.webhookUrl || '',
      botToken: '',
      clearBotToken: false,
    });
    setRuntimeModalOpen(true);
  };

  const submit = async () => {
    const validationError = validateForm(form);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    try {
      await api.createTelegramAccessRule({
        ...form,
        identifier: form.identityType === 'phone'
          ? cleanupPhone(form.identifier)
          : form.identifier.trim().replace(/\s+/g, ''),
      });
      setForm(INITIAL_FORM);
      await refresh();
      notify?.({ tone: 'success', title: 'Доступ збережено' });
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Не вдалося зберегти доступ');
    } finally {
      setSaving(false);
    }
  };

  const saveRuntimeSettings = async () => {
    setRuntimeSaving(true);
    try {
      const nextSnapshot = await api.updateTelegramRuntimeSettings({
        miniAppUrl: runtimeForm.miniAppUrl,
        webhookUrl: runtimeForm.webhookUrl,
        botToken: runtimeForm.botToken,
        clearBotToken: runtimeForm.clearBotToken,
      });
      setSnapshot(nextSnapshot);
      setRuntimeModalOpen(false);
      setError('');
      notify?.({ tone: 'success', title: 'Підключення Telegram збережено' });
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Не вдалося зберегти підключення Telegram');
    } finally {
      setRuntimeSaving(false);
    }
  };

  const updateRule = async (rule: TelegramAccessRule, patch: Partial<TelegramAccessRule>) => {
    try {
      await api.updateTelegramAccessRule(rule.id, patch);
      await refresh();
      notify?.({ tone: 'success', title: 'Правило оновлено' });
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Не вдалося оновити правило');
    }
  };

  const deleteRule = async () => {
    if (!deleteCandidate) return;

    setDeleting(true);
    try {
      await api.deleteTelegramAccessRule(deleteCandidate.id);
      setDeleteCandidate(null);
      await refresh();
      notify?.({ tone: 'success', title: 'Доступ видалено' });
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Не вдалося видалити доступ');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Page
      title="Telegram Mini App"
      subtitle="Хто може відкривати Mini App і що йому дозволено"
      primaryAction={{ content: 'Оновити', onAction: refresh, loading }}
    >
      <div className="admin-stack">
        {error ? (
          <Banner tone="critical" title="Telegram налаштування недоступні">
            <p>{error}</p>
          </Banner>
        ) : null}
        {readOnly ? (
          <Banner tone="info" title="Роль перегляду">
            <p>Підключення Telegram і правила доступу відкриті тільки для перегляду. Зміни, видалення й оновлення ключів вимкнені.</p>
          </Banner>
        ) : null}

        {loading && !snapshot ? (
          <Card>
            <div className="admin-stack">
              <SkeletonBodyText lines={6} />
            </div>
          </Card>
        ) : null}

        {snapshot ? (
          <>
            <div className="admin-grid admin-grid--metrics">
              <MetricCard
                label="Адміни"
                value={String(snapshot.summary.adminCount)}
                summary="Активні правила з правом змінювати налаштування"
                status={snapshot.summary.adminCount > 0 ? 'ok' : 'warning'}
              />
              <MetricCard
                label="Переглядачі"
                value={String(snapshot.summary.viewerCount)}
                summary="Активні правила тільки для перегляду"
                status="ok"
              />
              <MetricCard
                label="Підключення бота"
                value={snapshot.summary.botRuntimeConfigured && snapshot.summary.miniAppRuntimeConfigured ? 'Налаштовано' : 'Не налаштовано'}
                summary={runtimeStatusText(snapshot)}
                status={snapshot.summary.botRuntimeConfigured && snapshot.summary.miniAppRuntimeConfigured ? 'ok' : 'warning'}
              />
            </div>

            <fieldset className="admin-fieldset-reset" disabled={readOnly}>
              <div className="admin-grid admin-grid--two admin-telegram-grid">
              <Card>
                <div className="admin-stack admin-telegram-form">
                  <div>
                    <Text as="h2" variant="headingMd">Додати доступ</Text>
                    <Text as="p" variant="bodyMd" tone="subdued">
                      Вкажіть Telegram ID або телефон і роль. Це правило буде використане Mini App після перевірки Telegram.
                    </Text>
                  </div>
                  <MenuSelect
                    label="Тип ідентифікатора"
                    options={IDENTITY_OPTIONS}
                    value={form.identityType}
                    onChange={(value) => setForm((current) => ({
                      ...current,
                      identityType: value as IdentityType,
                      identifier: '',
                    }))}
                  />
                  <TextField
                    label="ID або номер телефону"
                    value={form.identifier}
                    onChange={(value) => setForm((current) => ({ ...current, identifier: value }))}
                    autoComplete="off"
                    placeholder={form.identityType === 'phone' ? '+380501112233' : '5025817871'}
                    helpText={helperText(form.identityType)}
                  />
                  <div className="admin-grid admin-grid--two">
                    <MenuSelect
                      label="Роль"
                      options={ROLE_OPTIONS}
                      value={form.role}
                      onChange={(value) => setForm((current) => ({ ...current, role: value as TelegramRole }))}
                    />
                    <MenuSelect
                      label="Статус"
                      options={STATUS_OPTIONS}
                      value={form.status}
                      onChange={(value) => setForm((current) => ({ ...current, status: value as RuleStatus }))}
                    />
                  </div>
                  <TextField
                    label="Назва / коментар"
                    value={form.label}
                    onChange={(value) => setForm((current) => ({ ...current, label: value }))}
                    autoComplete="off"
                    placeholder="Наприклад: власник, склад, менеджер"
                  />
                  <div className="admin-inline-actions">
                    <Button variant="primary" onClick={submit} loading={saving}>
                      Зберегти доступ
                    </Button>
                    <Button onClick={() => setForm(INITIAL_FORM)} disabled={saving}>
                      Очистити
                    </Button>
                  </div>
                </div>
              </Card>

              <Card>
                <div className="admin-stack">
                  <div className="admin-telegram-runtime-head">
                    <div>
                      <Text as="h2" variant="headingMd">Підключення Telegram</Text>
                      <Text as="p" variant="bodyMd" tone="subdued">
                        Тут зберігаються адреса Mini App, адреса подій бота і ключ бота. Ключ не показується відкрито.
                      </Text>
                    </div>
                    <div className="admin-telegram-runtime-head__action">
                      <Button onClick={openRuntimeModal}>Налаштувати</Button>
                    </div>
                  </div>
                  <ol className="admin-list">
                    <li>Mini App отримує службові дані від Telegram у момент відкриття.</li>
                    <li>Сервер перевіряє, що запуск справді прийшов із Telegram, а не з підробленого джерела.</li>
                    <li>Якщо Telegram ID уже є у дозволеному списку, користувач входить одразу.</li>
                    <li>Якщо ID немає, бот може попросити поділитися контактом і звіряє номер телефону.</li>
                    <li>Адмін може змінювати налаштування. Переглядач бачить стан, але не керує системою.</li>
                  </ol>
                  <div className="admin-telegram-runtime">
                    <div>
                      <span>Адреса Mini App</span>
                      <strong>{snapshot.runtime.miniAppUrlConfigured ? snapshot.runtime.miniAppUrl : 'Не налаштовано'}</strong>
                      <em>{sourceLabel(snapshot.runtime.miniAppUrlSource)}</em>
                    </div>
                    <div>
                      <span>Адреса подій бота</span>
                      <strong>{snapshot.runtime.webhookUrlConfigured ? snapshot.runtime.webhookUrl : 'Не налаштовано'}</strong>
                      <em>{sourceLabel(snapshot.runtime.webhookUrlSource)}</em>
                    </div>
                    <div>
                      <span>Ключ бота</span>
                      <strong>{snapshot.runtime.botTokenConfigured ? `Налаштовано · ${snapshot.runtime.botTokenFingerprint || 'без відбитка'}` : 'Не налаштовано'}</strong>
                      <em>{sourceLabel(snapshot.runtime.botTokenSource)}</em>
                    </div>
                  </div>
                </div>
              </Card>
              </div>

              <Card>
                <div className="admin-stack">
                  <div className="admin-section-head">
                    <div>
                      <Text as="h2" variant="headingMd">Правила доступу</Text>
                      <Text as="p" variant="bodyMd" tone="subdued">
                        Активних: {activeRules.length}. Вимкнені лишаються в історії налаштувань, але не дають доступ.
                      </Text>
                    </div>
                    <StatusBadge status={snapshot.summary.disabledCount > 0 ? 'warning' : 'ok'} label={`${snapshot.rules.length} правил`} />
                  </div>

                  {snapshot.rules.length === 0 ? (
                    <div className="admin-empty-state">
                      <Text as="p" variant="bodyMd">Правил ще немає. Додайте Telegram ID або телефон вище.</Text>
                    </div>
                  ) : (
                    <div className="admin-telegram-rule-list">
                      {snapshot.rules.map((rule) => (
                        <div key={rule.id} className="admin-telegram-rule">
                          <div className="admin-telegram-rule__identity">
                            <span>{identityLabel(rule.identityType)}</span>
                            <strong>{rule.identifier}</strong>
                            {rule.label ? <em>{rule.label}</em> : null}
                          </div>
                          <div className="admin-telegram-rule__meta">
                            <div>
                              <span>Роль</span>
                              <strong>{roleLabel(rule.role)}</strong>
                            </div>
                            <div>
                              <span>Статус</span>
                              <div className="admin-telegram-rule__status">{ruleStatusBadge(rule.status)}</div>
                            </div>
                            <div>
                              <span>Оновлено</span>
                              <strong>{formatDateTime(rule.updatedAt || rule.createdAt)}</strong>
                            </div>
                          </div>
                          <div className="admin-telegram-rule__actions">
                            <MenuSelect
                              label="Роль"
                              options={ROLE_ACTION_OPTIONS}
                              value={rule.role}
                              compact
                              onChange={(value) => updateRule(rule, { role: value as TelegramRole })}
                            />
                            <Button onClick={() => updateRule(rule, {
                              status: rule.status === 'active' ? 'disabled' : 'active',
                            })}>
                              {rule.status === 'active' ? 'Вимкнути' : 'Увімкнути'}
                            </Button>
                            <Button tone="critical" onClick={() => setDeleteCandidate(rule)}>
                              Видалити
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            </fieldset>

            <Modal
              open={runtimeModalOpen}
              onClose={() => setRuntimeModalOpen(false)}
              title="Змінити Telegram підключення"
              primaryAction={{
                content: 'Зберегти',
                onAction: saveRuntimeSettings,
                loading: runtimeSaving,
                disabled: readOnly,
              }}
              secondaryActions={[{
                content: 'Скасувати',
                onAction: () => setRuntimeModalOpen(false),
              }]}
            >
              <Modal.Section>
                <div className="admin-stack">
                  <Banner tone="info" title="Що тут змінюється">
                    <p>Адреси Mini App і подій бота зберігаються відкрито. Ключ бота зберігається як секрет: у списку видно тільки короткий відбиток для перевірки.</p>
                  </Banner>
                  <TextField
                    label="Адреса Mini App"
                    value={runtimeForm.miniAppUrl}
                    onChange={(value) => setRuntimeForm((current) => ({ ...current, miniAppUrl: value }))}
                    autoComplete="off"
                    placeholder="https://adminapp.pakolorente.com.ua"
                    helpText="Адреса, яку Telegram відкриватиме як Mini App."
                  />
                  <TextField
                    label="Адреса подій бота"
                    value={runtimeForm.webhookUrl}
                    onChange={(value) => setRuntimeForm((current) => ({ ...current, webhookUrl: value }))}
                    autoComplete="off"
                    placeholder="https://adminapp.pakolorente.com.ua/telegram/webhook"
                    helpText="Адреса, куди Telegram надсилатиме події бота."
                  />
                  <TextField
                    label="Ключ бота"
                    value={runtimeForm.botToken}
                    onChange={(value) => setRuntimeForm((current) => ({
                      ...current,
                      botToken: value,
                      clearBotToken: value ? false : current.clearBotToken,
                    }))}
                    autoComplete="off"
                    type="password"
                    placeholder={snapshot.runtime.botTokenConfigured ? 'Залиште порожнім, щоб не змінювати' : '123456:ABC...'}
                    helpText={snapshot.runtime.botTokenConfigured
                      ? `Зараз збережено ключ (${sourceLabel(snapshot.runtime.botTokenSource)}), відбиток: ${snapshot.runtime.botTokenFingerprint || '—'}.`
                      : 'Ключ від BotFather. Потрібен для перевірки запуску Mini App і подій бота.'}
                  />
                  {snapshot.runtime.botTokenConfigured ? (
                    <Checkbox
                      label="Стерти поточний ключ бота"
                      checked={runtimeForm.clearBotToken}
                      onChange={(checked) => setRuntimeForm((current) => ({
                        ...current,
                        clearBotToken: checked,
                        botToken: checked ? '' : current.botToken,
                      }))}
                    />
                  ) : null}
                </div>
              </Modal.Section>
            </Modal>

            <Modal
              open={Boolean(deleteCandidate)}
              onClose={() => setDeleteCandidate(null)}
              title="Видалити доступ?"
              primaryAction={{
                content: 'Видалити',
                onAction: deleteRule,
                loading: deleting,
                disabled: readOnly,
              }}
              secondaryActions={[{
                content: 'Скасувати',
                onAction: () => setDeleteCandidate(null),
              }]}
            >
              <Modal.Section>
                <Text as="p" variant="bodyMd">
                  {deleteCandidate
                    ? `Правило для ${deleteCandidate.identifier} буде видалене з доступу Mini App.`
                    : 'Правило буде видалене.'}
                </Text>
              </Modal.Section>
            </Modal>
          </>
        ) : null}
      </div>
    </Page>
  );
}
